import { useI18n } from '@vista-digital/occ-ui'
import type { FunctionComponent } from 'react'
import { memo, useMemo } from 'react'
import {
    createBrowserRouter,
    createMemoryRouter,
    RouterProvider,
    useLocation,
    useNavigate,
    useNavigationType,
} from 'react-router-dom'
import { Services } from '../services'
import { getLogger } from '../services/logger/logger.service'
import { getRoutes } from './getRoutes'
import type { LayoutOverrideProps } from './Layout'

const logger = getLogger('App')

export interface PagesRouterOverrideProps extends LayoutWrapperOverrideProps {
    overridePageComponent?: {
        pageKey: string
        component: FunctionComponent
    }
    overridePath?: string
    overrideSearchParams?: { [key: string]: string }
    Layout: FunctionComponent<LayoutOverrideProps>
}

export const PagesRouter: FunctionComponent<PagesRouterOverrideProps> = ({
    overridePageComponent,
    overridePath,
    overrideSearchParams,
    Layout,
    ...layoutOverrideProps
}) => {
    logger.value.trace('PagesRouter initialised', {
        overridePageComponent,
        overridePath,
        overrideSearchParams,
        ...layoutOverrideProps,
    })
    logger.value.debug('PagesRouter', Date.now() - window.loadMs)

    const i18n = useI18n()
    Services.UrlService.setI18n(i18n)

    const allRoutes = useMemo(() => getRoutes(overridePageComponent), [])
    logger.value.trace('All routes created', allRoutes)

    const LayoutWrapperMemo = useMemo(
        () => () => <LayoutWrapper Layout={Layout} {...layoutOverrideProps} />,
        [layoutOverrideProps, Layout]
    )

    const router = useMemo(() => {
        if (!allRoutes || allRoutes.length === 0) return null
        const routerArgs = [{ Component: LayoutWrapperMemo, children: allRoutes }]

        return overridePath
            ? createMemoryRouter(routerArgs, {
                  initialEntries: [
                      {
                          pathname: overridePath,
                          search: new URLSearchParams(overrideSearchParams ?? '').toString(),
                      },
                  ],
              })
            : createBrowserRouter(routerArgs)
    }, [overridePath, overrideSearchParams, allRoutes, LayoutWrapperMemo])

    if (allRoutes.length === 0 || !router) return null

    Services.NavigationService.setRoutes(allRoutes)

    return <RouterProvider router={router} />
}

interface LayoutWrapperOverrideProps extends LayoutOverrideProps {
    layoutOverride?: FunctionComponent
    Layout: FunctionComponent<LayoutOverrideProps>
}

export const LayoutWrapper: FunctionComponent<LayoutWrapperOverrideProps> = memo(props => {
    const { layoutOverride } = props
    const { Layout } = props

    const navigate = useNavigate()
    const location = useLocation()
    const navigationType = useNavigationType()

    const Element = useMemo(() => {
        logger.value.debug('Element memo recompute', Date.now() - window.loadMs)
        const { layoutOverride: _, Layout: __, ...normalLayoutOverrideProps } = props
        return layoutOverride ?? (() => <Layout {...normalLayoutOverrideProps} />)
    }, [props, layoutOverride, Layout])

    // Trailing slash not always added by React Router, but we always want to have one, especially for Cinemas, Popups and HelloBar
    if (!location.pathname.endsWith('/')) {
        location.pathname += '/'
        navigate(location, { replace: true })
        logger.value.debug('Redirecting to path with trailing slash', location.pathname)
        return null
    }

    Services.NavigationService.setNavigationType(navigationType)
    Services.NavigationService.setNavigate(navigate)
    Services.NavigationService.setLocation(location)

    return <Element />
})
